import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { GridColDef, GridValueFormatterParams } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ServerDataGrid } from "components/ui/ServerDataGrid";
import { COLUMN_DEFS } from "components/transactions/TransactionsList";
import dayjs, { Dayjs } from "dayjs";
import {
  Account,
  useListAccountsQuery,
  useListOrionLiveTransactionsQuery,
  useListOrionTransactionTypesQuery,
} from "features/trading/accounts";
import { OrionLiveTransactionParams, OrionTransactionType } from "helpers/types";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

const API_DATE_FORMAT = "YYYY-MM-DD";

const DISPLAY_DATE_FORMAT = "MM/DD/YYYY";

// The window the tab opens on. Orion is queried live for every change, so
// the range is always set -- and nothing is asked for until an account is
// picked, as a firm-wide query is too large for Orion to answer live.
const DEFAULT_WINDOW_DAYS = 7;

const FILTER_DEBOUNCE_MS = 250;

const ACCOUNT_SEARCH_PAGE_SIZE = 25;

// Orion answers the whole window at once, so the grid pages and sorts in the
// browser -- which makes every column sortable, unlike the Imported tab.
const COLUMNS: GridColDef[] = COLUMN_DEFS.map((col) => ({
  ...col,
  valueFormatter: ({ value }: GridValueFormatterParams) =>
    value === null || value === undefined ? "N/A" : value,
}));

const defaultDateFrom = () => dayjs().subtract(DEFAULT_WINDOW_DAYS, "day");

const accountLabel = (account: Account) =>
  `${account.account_number} (${account.custodian__name}) — ${account.client__name}`;

const errorMessage = (error: unknown) => {
  const detail = (error as FetchBaseQueryError | undefined)?.data as
    | { detail?: string }
    | undefined;
  return detail?.detail ?? "Orion could not be reached.";
};

export const OrionTransactionsList = () => {
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(defaultDateFrom);
  const [dateTo, setDateTo] = useState<Dayjs | null>(() => dayjs());
  const [account, setAccount] = useState<Account | null>(null);
  const [accountSearch, setAccountSearch] = useState("");
  const [types, setTypes] = useState<OrionTransactionType[]>([]);

  const [debouncedAccountSearch] = useDebounce(
    accountSearch,
    FILTER_DEBOUNCE_MS,
  );

  const { data: accounts, isFetching: accountsLoading } = useListAccountsQuery({
    filters: { search: debouncedAccountSearch.trim() },
    pagination: { page: 0, pageSize: ACCOUNT_SEARCH_PAGE_SIZE },
  });

  const {
    data: transactionTypes,
    isFetching: typesLoading,
    isError: typesFailed,
  } = useListOrionTransactionTypesQuery();

  const rangeValid = Boolean(
    dateFrom?.isValid() && dateTo?.isValid() && !dateFrom.isAfter(dateTo),
  );

  const params = useMemo<OrionLiveTransactionParams | null>(() => {
    if (!rangeValid || !dateFrom || !dateTo || !account?.orion_account_id) {
      return null;
    }
    const query: OrionLiveTransactionParams = {
      start_date: dateFrom.format(API_DATE_FORMAT),
      end_date: dateTo.format(API_DATE_FORMAT),
      account_id: account.orion_account_id,
    };
    if (types.length) {
      query.transaction_type_ids = types.map((type) => type.id).join(",");
    }
    return query;
  }, [rangeValid, dateFrom, dateTo, account, types]);

  const { data, isFetching, isError, error } =
    useListOrionLiveTransactionsQuery(
      params ?? { start_date: "", end_date: "", account_id: 0 },
      { skip: !params },
    );

  const resetFilters = () => {
    setDateFrom(defaultDateFrom());
    setDateTo(dayjs());
    setAccount(null);
    setAccountSearch("");
    setTypes([]);
  };

  const datePickerSlotProps = (helperText?: string) => ({
    textField: {
      size: "small" as const,
      sx: { width: 175 },
      required: true,
      error: Boolean(helperText),
      helperText,
    },
  });

  const rangeError = !dateFrom?.isValid()
    ? "Required"
    : !dateTo?.isValid()
      ? undefined
      : dateFrom.isAfter(dateTo)
        ? "After Date to"
        : undefined;

  return (
    <Box>
      <Typography variant="h6" color="primary.main" paddingX={0.5}>
        Orion Transactions (live)
      </Typography>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="flex-start"
        gap={1.5}
        paddingX={0.5}
        paddingY={1}
      >
        <DatePicker
          label="Date from"
          value={dateFrom}
          onChange={setDateFrom}
          format={DISPLAY_DATE_FORMAT}
          maxDate={dateTo ?? undefined}
          slotProps={datePickerSlotProps(rangeError)}
        />
        <DatePicker
          label="Date to"
          value={dateTo}
          onChange={setDateTo}
          format={DISPLAY_DATE_FORMAT}
          minDate={dateFrom ?? undefined}
          slotProps={datePickerSlotProps(
            dateTo?.isValid() ? undefined : "Required",
          )}
        />
        <Autocomplete
          size="small"
          sx={{ width: 340 }}
          options={accounts?.results ?? []}
          loading={accountsLoading}
          value={account}
          onChange={(_event, selected) => setAccount(selected)}
          // Only typing searches: picking an option writes its whole label
          // into the input, and searching on that would empty the list the
          // next time it opens.
          onInputChange={(_event, value, reason) => {
            if (reason !== "reset") {
              setAccountSearch(value);
            }
          }}
          // The server searches; the list is already filtered.
          filterOptions={(options) => options}
          getOptionLabel={accountLabel}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          // Orion filters on its own account id, so an account we never
          // matched to Orion cannot be asked for.
          getOptionDisabled={(option) => !option.orion_account_id}
          noOptionsText={
            accountSearch ? "No accounts found" : "Type to search accounts"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Account"
              placeholder="Search accounts"
            />
          )}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          sx={{ width: 320 }}
          options={transactionTypes ?? []}
          loading={typesLoading}
          value={types}
          onChange={(_event, selected) => setTypes([...selected])}
          getOptionLabel={(type) => type.name}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          limitTags={2}
          noOptionsText={
            typesFailed
              ? "Orion's transaction types could not be loaded"
              : "No transaction types"
          }
          renderOption={(props, type, { selected }) => {
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  size="small"
                  checked={selected}
                  sx={{ marginRight: 1 }}
                />
                {type.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Transaction Type"
              placeholder={types.length ? undefined : "All types"}
            />
          )}
        />
        <Button onClick={resetFilters} sx={{ marginTop: 0.25 }}>
          Reset filters
        </Button>
      </Stack>
      {!account && (
        // A prompt, not an alarm: MUI gives every Alert role="alert".
        <Alert
          severity="info"
          role="status"
          sx={{ marginX: 0.5, marginBottom: 1 }}
        >
          Select an account to load its Orion transactions.
        </Alert>
      )}
      {isError && params && (
        <Alert severity="error" sx={{ marginX: 0.5, marginBottom: 1 }}>
          {errorMessage(error)}
        </Alert>
      )}
      <ServerDataGrid
        sx={{ height: "calc(100vh - 300px)", minHeight: 420 }}
        columns={COLUMNS}
        rows={(params && !isError && data?.results) || []}
        loading={isFetching}
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
          sorting: { sortModel: [{ field: "transaction_date", sort: "desc" }] },
        }}
        pageSizeOptions={[25, 50, 100]}
        disableColumnFilter
      />
    </Box>
  );
};
